import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { BrainCircuitIcon, TrendingUpIcon, UsersIcon, TargetIcon, ZapIcon, ShieldIcon, LinkedinIcon, BriefcaseIcon, TrophyIcon, GraduationCapIcon } from 'lucide-react';
const principles = [{
  icon: TargetIcon,
  title: 'Thesis-Driven',
  description: 'Every strategy begins with a rigorous hypothesis and pre-registered success criteria.'
}, {
  icon: ZapIcon,
  title: 'Agile Execution',
  description: 'Small teams move faster, exploit micro edges, and iterate without institutional constraints.'
}, {
  icon: ShieldIcon,
  title: 'Risk-Aware',
  description: 'Comprehensive risk management with continuous monitoring and mandatory post-mortems.'
}];
const team = [{
  name: 'Trung Nguyen',
  role: 'Chief Investment Officer',
  degree: 'BSc Finance & Data Science',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wMkEhubvxoFoZTXc72Qxp2/Trung_Nguyen.jpg',
  linkedin: 'https://www.linkedin.com/in/realtrungnguyen',
  experiences: [{
    type: 'work',
    title: 'Asset Management',
    company: 'State Street Investment Management'
  }, {
    type: 'leadership',
    title: 'Founder',
    company: 'Aidworks Foundation (550+ families served, $13.5k+ annually)'
  }, {
    type: 'leadership',
    title: 'Co-Founder',
    company: 'Northeastern Investment Banking Group (Interim President | M&A Division)'
  }, {
    type: 'work',
    title: 'FX Sales & Trading Summer Analyst',
    company: 'Orient Commercial Bank (OCB) - Institutional Sales'
  }, {
    type: 'work',
    title: 'Investment Summer Analyst',
    company: 'Vietnam Ministry of Finance (HCMC Finance & Investment SOE)'
  }, {
    type: 'work',
    title: 'Hedge Fund Equity Research Intern',
    company: 'VinaCapital ($4B AUM Mega-fund)'
  }, {
    type: 'work',
    title: 'Private Equity Extern',
    company: 'Project Destined (Retail Real Estate Group)'
  }, {
    type: 'work',
    title: 'Spring Private Equity Analyst',
    company: 'CPG Family Office (Multi-Family & Mixed Use Real Estate)'
  }, {
    type: 'work',
    title: 'Fall Equity Research Extern',
    company: 'Westlake Securities (Middle-Market Investment Bank)'
  }, {
    type: 'work',
    title: 'Corporate VC & Business Analytics Extern',
    company: 'HP (Series A & B, AI startups)'
  }, {
    type: 'achievement',
    title: 'Various Insight Programs',
    company: 'Goldman Sachs'
  }]
}, {
  name: 'Alejandro Vides',
  role: 'Chief Research Officer',
  degree: 'BSc Math & Philosophy',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/czY7e6EnsToqT7uUZMR3Vg/Ale_picture.jpg',
  linkedin: 'https://www.linkedin.com/in/a-vides/',
  experiences: [{
    type: 'leadership',
    title: 'Co-Founder',
    company: 'The Daily Benji (Fintech - Financial awareness via email)'
  }, {
    type: 'work',
    title: 'Summer Analyst',
    company: 'EKANTA Wealth Advisors'
  }, {
    type: 'leadership',
    title: 'Co-Founder',
    company: 'The Life Studio (Won US Congressional App Challenge)'
  }, {
    type: 'achievement',
    title: 'Crypto & NFT Trader',
    company: '$60k profit from $300 seed (200x return, $1.9M+ volume)'
  }, {
    type: 'work',
    title: 'Youth Sailing Coach',
    company: 'Coconut Grove Sailing Club Foundation'
  }, {
    type: 'achievement',
    title: 'Reseller',
    company: '$25k+ revenue across multiple product categories'
  }]
}, {
  name: 'Tivin Wu Lin',
  role: 'Chief Operating Officer',
  degree: 'BSBA Finance, Minor in Math',
  image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/dGV8hmNmpe7WhUmPS6ySin/Tivin_picture.jpg',
  linkedin: 'https://www.linkedin.com/in/tivinwulin/',
  experiences: [{
    type: 'leadership',
    title: 'Mentorship Content & Engagement Coordinator',
    company: 'International Students in Business (ISIB)'
  }, {
    type: 'leadership',
    title: 'Teacher Assistant',
    company: 'Northeastern (6+ hrs/week, graded 300+ case studies)'
  }, {
    type: 'leadership',
    title: 'Finance Chair',
    company: 'Northeastern ($5000+ funding requests, 4-member board)'
  }, {
    type: 'work',
    title: 'Legislative Intern',
    company: 'Costa Rica Government (Drafted 15+ weekly briefs)'
  }, {
    type: 'work',
    title: 'Senior Manager',
    company: 'Mercado Mayorista AV6 (35+ staff, $5M+ inventory)'
  }, {
    type: 'work',
    title: 'Accounting Assistant',
    company: 'GBC ASESORES ECONOMICOS ($100M+ transactions processed)'
  }, {
    type: 'leadership',
    title: 'Hispanic Honors Society President',
    company: 'Maranatha High School (Raised $2500+, 40+ members)'
  }, {
    type: 'leadership',
    title: 'Math Club President',
    company: 'Maranatha High School (400+ Pi-day attendees)'
  }]
}];
const getExperienceIcon = (type: string) => {
  switch (type) {
    case 'work':
      return BriefcaseIcon;
    case 'achievement':
      return TrophyIcon;
    case 'leadership':
      return GraduationCapIcon;
    default:
      return BriefcaseIcon;
  }
};
export function About() {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [expandedMember, setExpandedMember] = useState<number | null>(null);
  return <section id="about" ref={ref} className="w-full py-32 px-8 bg-white" data-id="element-10">
      <div className="max-w-7xl mx-auto" data-id="element-11">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-20" data-id="element-12">
          <div className="inline-block px-4 py-2 bg-blue-50 rounded-full mb-4" data-id="element-13">
            <span className="text-sm font-semibold text-blue-600 tracking-wider uppercase" data-id="element-14">
              About Us
            </span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 tracking-tight" data-id="element-15">
            Independent Research Lab
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed" data-id="element-16">
            We apply institutional-grade quantitative methods to illiquid and
            overlooked markets that are often too small or too complex for
            traditional funds, combining rigorous research with systematic
            modeling and disciplined risk management.
          </p>
        </motion.div>
        {/* Three Column Layout */}
        <div className="grid md:grid-cols-3 gap-8 mb-32" data-id="element-17">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} className="bg-slate-50 rounded-2xl p-8 border border-slate-200" data-id="element-18">
            <BrainCircuitIcon className="w-12 h-12 text-slate-900 mb-6" strokeWidth={1.5} data-id="element-19" />
            <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-20">
              Who We Are
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-21">
              An independent, student-run quantitative research lab based in
              Boston, applying institutional-grade finance, mathematics,
              computer science, and data science to overlooked markets.
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="bg-slate-50 rounded-2xl p-8 border border-slate-200" data-id="element-22">
            <TrendingUpIcon className="w-12 h-12 text-slate-900 mb-6" strokeWidth={1.5} data-id="element-23" />
            <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-24">
              What We Do
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-25">
              Develop strategies with durable edge through rigorous research,
              systematic modeling, and disciplined risk management in markets
              too small or too complex for traditional funds.
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={inView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.3
        }} className="bg-slate-50 rounded-2xl p-8 border border-slate-200" data-id="element-26">
            <UsersIcon className="w-12 h-12 text-slate-900 mb-6" strokeWidth={1.5} data-id="element-27" />
            <h3 className="text-2xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-28">
              Why Stay Small
            </h3>
            <p className="text-slate-600 leading-relaxed" data-id="element-29">
              Intentionally maintain a small and selective team to move quickly,
              preserve alpha, and maintain clear accountability for every
              position we develop.
            </p>
          </motion.div>
        </div>
        {/* Principles Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.4
      }} className="mb-32" data-id="element-30">
          <h3 className="text-3xl font-bold text-slate-900 mb-12 text-center tracking-tight" data-id="element-31">
            Our Principles
          </h3>
          <div className="grid md:grid-cols-3 gap-6" data-id="element-32">
            {principles.map(principle => {
            const Icon = principle.icon;
            return <div key={principle.title} className="bg-white border-2 border-slate-200 rounded-xl p-8 hover:border-slate-900 transition-all" data-id="element-33">
                  <Icon className="w-10 h-10 text-slate-900 mb-4" strokeWidth={1.5} data-id="element-34" />
                  <h4 className="text-xl font-bold text-slate-900 mb-3 tracking-tight" data-id="element-35">
                    {principle.title}
                  </h4>
                  <p className="text-slate-600 leading-relaxed" data-id="element-36">
                    {principle.description}
                  </p>
                </div>;
          })}
          </div>
        </motion.div>
        {/* Leadership Team Section */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={inView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6,
        delay: 0.5
      }} data-id="element-37">
          <div className="text-center mb-16" data-id="element-38">
            <h3 className="text-3xl font-bold text-slate-900 mb-4 tracking-tight" data-id="element-39">
              Leadership Team
            </h3>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-40">
              Experienced leaders driving quantitative research and operational
              excellence
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-12" data-id="element-41">
            {team.map((member, index) => {
            const isExpanded = expandedMember === index;
            return <motion.div key={member.name} initial={{
              opacity: 0,
              y: 20
            }} animate={inView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.4,
              delay: 0.6 + index * 0.1
            }} className="group" data-id="element-42">
                  <div className="relative aspect-square mb-6 rounded-2xl overflow-hidden bg-slate-100" data-id="element-43">
                    <img src={member.image} alt={member.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-44" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-45" />
                  </div>
                  <div className="space-y-2" data-id="element-46">
                    <div className="flex items-start justify-between" data-id="element-47">
                      <h4 className="text-xl font-bold text-slate-900 tracking-tight" data-id="element-48">
                        {member.name}
                      </h4>
                      <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="text-slate-600 hover:text-blue-600 transition-colors" data-id="element-49">
                        <LinkedinIcon className="w-5 h-5" data-id="element-50" />
                      </a>
                    </div>
                    <p className="text-sm font-semibold text-blue-600 tracking-wide uppercase" data-id="element-51">
                      {member.role}
                    </p>
                    <p className="text-sm text-slate-600" data-id="element-52">{member.degree}</p>
                    <button onClick={() => setExpandedMember(isExpanded ? null : index)} className="text-sm text-slate-900 hover:text-blue-600 transition-colors flex items-center gap-2 font-semibold pt-2" data-id="element-53">
                      {isExpanded ? 'Hide' : 'View'} Experience
                      <motion.span animate={{
                    rotate: isExpanded ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-54">
                        ▼
                      </motion.span>
                    </button>
                  </div>
                  <AnimatePresence data-id="element-55">
                    {isExpanded && <motion.div initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.4
                }} className="mt-4 bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" data-id="element-56">
                        <div className="p-6 space-y-3 max-h-96 overflow-y-auto" data-id="element-57">
                          {member.experiences.map((exp, expIndex) => {
                      const Icon = getExperienceIcon(exp.type);
                      return <motion.div key={expIndex} initial={{
                        opacity: 0,
                        x: -20
                      }} animate={{
                        opacity: 1,
                        x: 0
                      }} transition={{
                        duration: 0.3,
                        delay: expIndex * 0.05
                      }} className="flex items-start gap-3 p-3 bg-white rounded-lg border border-slate-200 hover:border-slate-900 transition-colors" data-id="element-58">
                                <div className="mt-1" data-id="element-59">
                                  <Icon className="w-5 h-5 text-slate-900" data-id="element-60" />
                                </div>
                                <div data-id="element-61">
                                  <p className="text-sm font-semibold text-slate-900" data-id="element-62">
                                    {exp.title}
                                  </p>
                                  <p className="text-xs text-slate-600" data-id="element-63">
                                    {exp.company}
                                  </p>
                                </div>
                              </motion.div>;
                    })}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </motion.div>;
          })}
          </div>
        </motion.div>
      </div>
    </section>;
}